import { createCreatorProductStore } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/createStore.ts';
import { MODE } from '@services/MarppleShopStudio/widget/CreatorProduct/core/types/types.ts';
import { html, View } from 'rune-ts';
import {
  StockInput,
  stockInputFactory,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Stock/StockInputFactory.ts';
import { BaseSection } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/BaseSection.ts';
import {
  ChangeSpoItemValue,
  ChangeSkuVolume,
  DeleteSpoItems,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Stock/events.ts';
import { formKeyMap } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/shared.ts';
import {
  ApplyNewSpoItems,
  Input_AddOption,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_AddOption/Input_AddOption.ts';
import { StockType } from '@services/MarppleShopStudio/widget/CreatorProduct/core/zod-schema/constants.ts';
import {
  toSpoItems,
  toSpoItemsIntl,
  extractSpoOptions,
  updateSpoItems,
  updateSpoIntlItems,
  subscribeStore,
  extractSpoIntlOptions,
  createSubscribeStoreList,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/helper.ts';
import { Input_StockThresholdSelector } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_StockThresholdSelector.ts';
import { CreatorProductState } from '@services/MarppleShopStudio/widget/CreatorProduct/core/store/types.ts';
import {
  StockInputValue,
  StockInputValue_Edit_Option_Intl,
} from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Stock/types.ts';
import { compact, every, isEmpty, pipe, toArray } from '@fxts/core';
import common_klass from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/section-views/styles/common.module.scss';
import { shareFont } from '@services/MarppleShop/shared/typography';
import { Tooltip } from '@services/MarppleShop/renderApp/components/atoms/Tooltip/Tooltip.ts';
import { required_html } from '@services/MarppleShopStudio/shared/component/atom/Input/core/helper.ts';
import { Input_Stock_Edit_Option_Intl } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Stock/Input_Stock_Edit_Option_Intl.ts';
import { Input_Stock_EditNoOption_Intl } from '@services/MarppleShopStudio/widget/CreatorProduct/ui/ProductForm/input-views/Input_Stock/Input_Stock_Edit_NoOption_Intl.ts';
import { ModalClosedEvent } from '@services/MarppleShop/renderApp/components/atoms/Modal/Modal.ts';
import { MShopUtilF } from '@modules/MShop/Util/F/Function/module/MShopUtilF';

interface StockAndOptionSectionData<T extends MODE> {
  store: ReturnType<typeof createCreatorProductStore<T>>;
  is_mobile?: boolean;
  is_created_sku?: boolean;
  is_studio_worker_admin?: boolean;
}

export class StockAndOptionSection<T extends MODE> extends BaseSection<T, StockAndOptionSectionData<T>> {
  protected readonly sectionTitle = '재고/옵션 정보';
  private store = this.data.store;
  private subscribeStoreList = createSubscribeStoreList();

  public override inputViews(): Array<View> {
    return pipe(
      [
        this.createStockInput(),
        this.data.is_created_sku
          ? null
          : new Input_AddOption({
              name: formKeyMap.spo_items,
              value: this.store.formData._.product._.spo._.spo_options,
              title: '옵션 추가',
              is_intl: this.store.mode === MODE.EDIT_TPL_INTL,
              is_mobile: this.data.is_mobile,
            }),
        new Input_StockThresholdSelector({
          name: formKeyMap.low_stock_threshold,
          value: this.store.formData.meta.low_stock_threshold ?? null,
          title: html`
            <div class="${common_klass.title_with_tooltip}">
              <span class="${common_klass.text} ${shareFont('14_bold')}">품절임박 표시</span>
              ${required_html}
              ${new Tooltip({
                id: formKeyMap.low_stock_threshold,
                content: html`
                  <div class="${common_klass.tooltip_content_container} ${shareFont('14_medium')}">
                    <span>선택한 조건에 따라 상품카드와 상세 페이지에 남은 수량을 노출합니다.</span>
                  </div>
                `,
                size: 'x_small',
                variant: 'studio',
              })}
            </div>
          `,
          setField: this.store.actions.setField,
        }),
      ],
      compact,
      toArray,
    );
  }

  protected override onRender() {
    this.addEventListener(ChangeSpoItemValue, this.changeSpoItemValue);

    this.addEventListener(ChangeSkuVolume, this.changeSkuVolume);

    this.addEventListener(ApplyNewSpoItems, this.applyNewSpoItems);

    this.addEventListener(DeleteSpoItems, this.deleteSpoItems);

    this.addEventListener(ModalClosedEvent, () => {
      /**
       * 체적정보 등록 팝업을 닫아도 body 픽스를 유지하기 위해 강제로 고정
       * 모바일에서는 body 픽스 고정 유지 안해도됨
       */
      if (!this.data.is_mobile) {
        MShopUtilF.bodyFixed$(true);
      }
    });

    /**
     * 해외 배송 모드에서 상품 체적 정보 에러 핸들링
     */
    this.handleSpoIntlVolume(true);
  }

  private changeSpoItemValue(e: ChangeSpoItemValue) {
    this.store.actions.setField(
      formKeyMap.spo_items,
      this.isIntlFormData(this.store.formData, this.store.mode) && this.isSpoIntlItem(e.detail.stock_values)
        ? updateSpoIntlItems(this.store.formData._.product._.spo._.spo_items, e.detail.stock_values)
        : updateSpoItems(this.store.formData._.product._.spo._.spo_items, e.detail.stock_values),
    );
  }

  private changeSkuVolume(e: ChangeSpoItemValue) {
    this.store.actions.setField(
      formKeyMap.spo_items,
      this.isIntlFormData(this.store.formData, this.store.mode) && this.isSpoIntlItem(e.detail.stock_values)
        ? updateSpoIntlItems(this.store.formData._.product._.spo._.spo_items, e.detail.stock_values)
        : updateSpoItems(this.store.formData._.product._.spo._.spo_items, e.detail.stock_values),
    );

    this.redraw();
    this.handleSpoIntlVolume(!!this.store.errors?.width);
  }

  private applyNewSpoItems(e: ApplyNewSpoItems) {
    /* spo_options 갱신 */
    this.store.actions.setField(formKeyMap.spo_options, e.detail.spo_options);

    const error = this.store.errors[formKeyMap.spo_options];
    if (error) {
      this.subView(Input_AddOption)?.setError(true, error);
      return;
    }

    /* spo_items 갱신 */
    this.store.actions.setField(
      formKeyMap.spo_items,
      this.isIntlFormData(this.store.formData, this.store.mode)
        ? toSpoItemsIntl(this.store.formData._.product._.spo._.spo_options)
        : toSpoItems(this.store.formData._.product._.spo._.spo_options),
    );

    /* stock_type 갱신 */
    this.store.actions.setField(
      formKeyMap.stock_type,
      this.store.formData._.product._.spo._.spo_items.length ? StockType.OPTION_STOCK : StockType.TOTAL_STOCK,
    );

    if (this.isIntlFormData(this.store.formData, this.store.mode)) {
      this.store.actions.setError(formKeyMap.width, null);
    }

    this.redraw();
    this.handleSpoIntlVolume(false);
  }

  private deleteSpoItems(e: DeleteSpoItems) {
    /* spo_items 삭제 */
    const deleteIndexSet = new Set(e.detail.delete_target_indexes);
    const spoItems = this.store.formData._.product._.spo._.spo_items.filter((_, i) => !deleteIndexSet.has(i));

    if (isEmpty(spoItems)) {
      /* spo_items 갱신 */
      const rawItem = {
        price: 0,
        sku: { name: '', nickname: '' },
        _: { spo_item_stock: { quantity: 0 } },
        sold_quantity: 0,
        group_names_option_names: null,
      };

      this.store.actions.setField(formKeyMap.spo_items, [rawItem]);
      /* spo_options 갱신 */
      this.store.actions.setField(formKeyMap.spo_options, []);
      /* stock_type 갱신 */
      this.store.actions.setField(
        formKeyMap.stock_type,
        isEmpty(spoItems) ? StockType.TOTAL_STOCK : StockType.OPTION_STOCK,
      );
    } else {
      /* spo_items 갱신 */
      this.store.actions.setField(
        formKeyMap.spo_items,
        this.store.formData._.product._.spo._.spo_items.filter((_, i) => !deleteIndexSet.has(i)),
      );
      /* spo_options 갱신 */
      this.store.actions.setField(
        formKeyMap.spo_options,
        this.isIntlFormData(this.store.formData, this.store.mode)
          ? extractSpoIntlOptions(
              this.store.formData._.product._.spo._.spo_items,
              this.store.formData._.product._.spo._.spo_options,
            )
          : extractSpoOptions(
              this.store.formData._.product._.spo._.spo_items,
              this.store.formData._.product._.spo._.spo_options,
            ),
      );
      /* stock_type 갱신 */
      this.store.actions.setField(
        formKeyMap.stock_type,
        this.store.formData._.product._.spo._.spo_items.length
          ? StockType.OPTION_STOCK
          : StockType.TOTAL_STOCK,
      );
    }

    this.redraw();
    this.handleSpoIntlVolume(!!this.store.errors?.width);
  }

  private handleSpoIntlVolume(is_error?: boolean) {
    if (this.store.mode === MODE.EDIT_TPL_INTL) {
      pipe(
        [
          this.subView(Input_Stock_EditNoOption_Intl),
          this.subView(Input_Stock_Edit_Option_Intl),
          this.subView(Input_AddOption),
        ],
        compact,
        toArray,
        (inputs) => {
          const stockInput = inputs.find(
            (input) =>
              input instanceof Input_Stock_EditNoOption_Intl || input instanceof Input_Stock_Edit_Option_Intl,
          );
          const addOptionInput = inputs.find((input) => input instanceof Input_AddOption);

          if (!(stockInput && addOptionInput)) return;

          if (!this.subscribeStoreList.has(formKeyMap.width)) {
            this.subscribeStoreList.add(formKeyMap.width);
            subscribeStore(this.store, {
              [formKeyMap.width]: stockInput,
            });
          } else {
            stockInput.setError(is_error);
          }

          if (!this.subscribeStoreList.has(formKeyMap.group_name_en)) {
            this.subscribeStoreList.add(formKeyMap.group_name_en);
            subscribeStore(this.store, {
              [formKeyMap.group_name_en]: addOptionInput,
            });
          }
        },
      );
    }
  }

  private createStockInput(): StockInput {
    const mode = this.store.mode;
    const spo = this.store.formData._.product._.spo;
    const stockType = spo.stock_type;
    const readonly = this.data.is_created_sku;
    return stockInputFactory.create(mode, spo, stockType, readonly, this.data.is_studio_worker_admin);
  }

  private isIntlFormData(
    formData: CreatorProductState<T>['formData'],
    mode: MODE,
  ): formData is CreatorProductState<typeof MODE.EDIT_TPL_INTL>['formData'] {
    return formData && mode === MODE.EDIT_TPL_INTL;
  }

  private isSpoIntlItem(spo_items: StockInputValue[]): spo_items is StockInputValue_Edit_Option_Intl[] {
    return pipe(
      spo_items,
      every((spo_item) => 'volume' in spo_item),
    );
  }
}
